/**
 * Audio ダウンロードのファイル名を作る純関数 (計画 1.2 / 8.5 の M3)。
 *
 * ## 命名規則
 *
 * ```
 * {放送タイトル}_{候補タイトル}.mp3   例) 遅延案内_駅名強調.mp3
 * audio_{候補タイトル}.mp3            例) audio_駅名強調.mp3 (放送タイトルが取れない場合)
 * ```
 *
 * ## なぜ純関数に切り出すか
 *
 * ファイル名規則は **解釈違いが出やすい** 箇所 (どこで切るか / 記号をどうするか)。
 * UI から独立させてテストで固定しておけば、後から見た人が
 * 「この名前で合っているのか」を実装を読まずに判断できる。
 *
 * ## ⚠️ 既存 `sanitizeFileNamePart` を流用しない理由 (計画 R12)
 *
 * 既存の TTS 比較検証側の関数は **ASCII 前提** で、日本語を落としてしまう。
 * 本機能のファイル名は日本語 (`遅延案内_駅名強調.mp3`) が要件なので、
 * 「OS が禁止する文字だけを除く」別関数として作る。
 * 既存関数を書き換えると比較検証側のテスト (ASCII 検査) と衝突する。
 *
 * ## ⚠️ バージョン サフィックス (`_v2`) は付けない
 *
 * MVP は履歴を持たない (計画 0.7-(1))。同名で 2 回保存した場合は
 * **ブラウザの `(1)` 付与に委ねる**。画面が採番すると「履歴があるのか」と
 * 誤解され、実際には戻せないという期待ギャップになる。
 */

/** 音声ファイルの拡張子 (Polly の出力は MP3 固定)。 */
export const AUDIO_FILE_EXTENSION = '.mp3';

/**
 * 放送タイトルが取れないときに使う代替語。
 *
 * 空文字にして `_駅名強調.mp3` のように先頭が区切り文字になるのを防ぐ。
 * 依頼の指定どおり `audio` を使う (例: `audio_駅名強調.mp3`)。
 */
export const FALLBACK_BROADCAST_TITLE = 'audio';

/**
 * 候補タイトルが空だったときの代替語 (計画リスク R2 の最終防衛)。
 *
 * サーバ側 (`parse_samples_json`) が空タイトルを `パターン1..5` に
 * 差し替えるため通常は使われない。ここで受けるのは
 * 「サーバの防衛が外れても壊れたファイル名 (`遅延案内_.mp3`) を作らない」ため。
 */
export const FALLBACK_CANDIDATE_TITLE = '候補';

/**
 * ファイル名の各部の長さ上限 (文字)。
 *
 * 放送テキストは最大 3000 字あり、そのまま使うと OS のファイル名上限
 * (多くの環境で 255 バイト) を超えて保存できない。
 * 「何の放送か」が分かれば十分なので短く切る。
 */
export const MAX_FILE_NAME_PART_LENGTH = 30;

/**
 * ファイル名に使えない文字を落とす (**日本語は残す**)。
 *
 * 除くのは以下だけ。
 *
 *   - OS がパス区切り / 予約記号として扱う文字 `\ / : * ? " < > |`
 *   - 制御文字 (改行を含む。ファイル名に入ると保存できない環境がある)
 *   - 先頭・末尾の `.` と空白 (Windows で末尾ドットのファイルが作れない)
 *
 * ⚠️ 記号を「安全そうな文字だけ許可する」方式にはしない。
 * 日本語の句読点や中黒まで落ちて「延案内駅名強調」のような読めない名前になり、
 * どの放送の音声か判別できなくなる。
 *
 * @param raw タイトル等の生の文字列
 * @returns ファイル名の一部として使える文字列 (使える文字が無ければ空文字)
 */
export function sanitizeFileNameSegment(raw: string): string {
  const withoutForbidden = raw
    // eslint-disable-next-line no-control-regex -- 制御文字はファイル名に入ると保存できないため明示的に除く
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[\\/:*?"<>|]/g, '')
    // 連続する空白は 1 つに畳む (改行を含む入力で名前が間延びするのを防ぐ)。
    .replace(/\s+/g, ' ')
    .trim();
  /*
   * 先頭・末尾の `.` を落とす。
   * `..mp3` のような名前や、末尾ドットで保存できない環境 (Windows) を避ける。
   */
  return withoutForbidden.replace(/^\.+/, '').replace(/\.+$/, '').trim();
}

/**
 * 放送テキストから **放送タイトル** (ファイル名の前半) を作る。
 *
 * 放送文は最大 3000 字あり、全文をファイル名にはできない。
 * 「何の放送か」が分かれば足りるので、**文の先頭だけ** を切り出す。
 *
 *   1. 最初の句読点 / 改行までを候補にする (文の途中で切れるのを避ける)
 *   2. ファイル名に使えない文字を落とす
 *   3. {@link MAX_FILE_NAME_PART_LENGTH} 文字で切る
 *
 * @param broadcastText Step1 の放送テキスト (未入力なら `null` / `undefined`)
 * @returns 放送タイトル。取り出せなければ空文字 (呼び出し側が代替語を使う)
 */
export function deriveBroadcastTitle(broadcastText: string | null | undefined): string {
  if (broadcastText === null || broadcastText === undefined) {
    return '';
  }
  /*
   * 句読点・改行で切るのは、`この電車は、3分遅れで…` のような文が
   * 途中で不自然に切れるのを防ぐため (先頭の一節が「何の放送か」を表す)。
   */
  const firstSegment = broadcastText.split(/[。．.、，,\r\n]/)[0] ?? '';
  return sanitizeFileNameSegment(firstSegment).slice(0, MAX_FILE_NAME_PART_LENGTH);
}

/** {@link buildAudioFileName} の引数。 */
export interface BuildAudioFileNameParams {
  /**
   * 放送テキスト (Step1 の入力)。
   *
   * 未解析 / 空の場合は {@link FALLBACK_BROADCAST_TITLE} (`audio`) を使う。
   */
  broadcastText?: string | null;
  /** 候補タイトル (AI が決めた「駅名強調」等) */
  candidateTitle: string;
}

/**
 * Audio ダウンロードのファイル名を作る。
 *
 * ```
 * 遅延案内_駅名強調.mp3        放送タイトルが取れる場合
 * audio_駅名強調.mp3           放送タイトルが取れない場合
 * ```
 *
 * ⚠️ **必ず拡張子まで含めて返す**。呼び出し側で `+ '.mp3'` させると
 * 付け忘れた箇所だけ拡張子なしで保存され、再生できないファイルができる。
 */
export function buildAudioFileName({
  broadcastText,
  candidateTitle,
}: BuildAudioFileNameParams): string {
  const heading = deriveBroadcastTitle(broadcastText);
  // 空なら `audio`。`_駅名強調.mp3` のように区切り文字から始まる名前を作らない。
  const prefix = heading === '' ? FALLBACK_BROADCAST_TITLE : heading;
  const sanitizedTitle = sanitizeFileNameSegment(candidateTitle).slice(
    0,
    MAX_FILE_NAME_PART_LENGTH,
  );
  /*
   * ⚠️ 候補タイトルの空は **サーバ側で潰されている** 想定 (R2) だが、
   * ここでも代替語を当てる。防衛が外れたときに `遅延案内_.mp3` という
   * 壊れた名前で保存され、後から中身を判別できなくなるのを防ぐ。
   */
  const suffix = sanitizedTitle === '' ? FALLBACK_CANDIDATE_TITLE : sanitizedTitle;
  return `${prefix}_${suffix}${AUDIO_FILE_EXTENSION}`;
}
